import { create } from 'zustand';
import { useProfileClassOrganizationStore } from '@/features/profile/classes/profileClassOrganizationStore';
import { CLASS_DOT_COLORS } from '@/features/class/manage/constants';
import { classCodeFromName, ensureUniqueClassCode } from '@/features/class/manage/classCodeUtils';
import {
  managedClassToCatalogEntry,
  managedClassToOwnedCard,
} from '@/features/class/manage/classMappers';
import {
  findOptionLabel,
  MOCK_ACADEMIC_YEARS,
  MOCK_GRADE_LEVELS,
  MOCK_SUBJECTS,
} from '@/features/class/manage/mockFormOptions';
import { seedManagedClasses } from '@/features/class/manage/seedManagedClasses';
import type { ClassFormMode, ClassFormValues, ManagedClass } from '@/features/class/manage/types';
import type { ClassCatalogEntry } from '@/features/class/classCatalog';
import type { OwnedClassCard } from '@/features/profile/types';

interface ClassManageState {
  classes: ManagedClass[];
  modalMode: ClassFormMode | null;
  editingClassId: string | null;
  sharingClassId: string | null;
  openCreateClass: () => void;
  openEditClass: (classId: string) => void;
  openShareClass: (classId: string) => void;
  closeShareClass: () => void;
  closeClassModal: () => void;
  createClass: (values: ClassFormValues) => ManagedClass;
  updateClass: (classId: string, values: ClassFormValues) => void;
  archiveClass: (classId: string) => void;
  archiveClasses: (classIds: readonly string[]) => void;
  deleteClass: (classId: string) => void;
  deleteClasses: (classIds: readonly string[]) => void;
  restoreClass: (classId: string) => void;
  getActiveOwnedCatalogEntries: () => readonly ClassCatalogEntry[];
  getActiveOwnedClassCards: () => readonly OwnedClassCard[];
  getArchivedClasses: () => readonly ManagedClass[];
  getManagedClassByCode: (code: string) => ManagedClass | undefined;
  getManagedClassById: (id: string) => ManagedClass | undefined;
}

function pickDotColor(existing: readonly ManagedClass[]): string {
  const used = new Set(existing.map((item) => item.dotClassName));
  const available = CLASS_DOT_COLORS.find((color) => !used.has(color));
  return available ?? CLASS_DOT_COLORS[existing.length % CLASS_DOT_COLORS.length];
}

function buildManagedClassFromForm(
  values: ClassFormValues,
  existing: readonly ManagedClass[],
  overrides: Partial<ManagedClass> & Pick<ManagedClass, 'id' | 'code'>,
): ManagedClass {
  const name = values.name.trim();
  const creditsRaw = values.credits.trim();
  const credits = creditsRaw ? Number(creditsRaw) : undefined;
  const subjectId = values.subjectId || undefined;

  return {
    id: overrides.id,
    code: overrides.code,
    name,
    classType: values.classType as ManagedClass['classType'],
    academicYearId: values.academicYearId,
    academicYearLabel:
      findOptionLabel(MOCK_ACADEMIC_YEARS, values.academicYearId) ?? values.academicYearId,
    subjectId,
    subjectLabel: subjectId ? findOptionLabel(MOCK_SUBJECTS, subjectId) : undefined,
    gradeLevelId: values.gradeLevelId,
    gradeLevelLabel: findOptionLabel(MOCK_GRADE_LEVELS, values.gradeLevelId) ?? values.gradeLevelId,
    credits: credits !== undefined && Number.isFinite(credits) ? credits : undefined,
    isPrivate: values.isPrivate,
    archived: overrides.archived ?? false,
    postCount: overrides.postCount ?? 0,
    memberCount: overrides.memberCount ?? 1,
    dotClassName: overrides.dotClassName ?? pickDotColor(existing),
  };
}

export const useClassManageStore = create<ClassManageState>((set, get) => ({
  classes: seedManagedClasses(),
  modalMode: null,
  editingClassId: null,
  sharingClassId: null,

  openCreateClass: () => {
    set({ modalMode: 'create', editingClassId: null, sharingClassId: null });
  },

  openEditClass: (classId) => {
    set({ modalMode: 'edit', editingClassId: classId, sharingClassId: null });
  },

  openShareClass: (classId) => {
    set({ sharingClassId: classId, modalMode: null, editingClassId: null });
  },

  closeShareClass: () => {
    set({ sharingClassId: null });
  },

  closeClassModal: () => {
    set({ modalMode: null, editingClassId: null });
  },

  createClass: (values) => {
    const existing = get().classes;
    const baseCode = classCodeFromName(values.name);
    const code = ensureUniqueClassCode(
      baseCode,
      existing.map((item) => item.code),
    );
    const created = buildManagedClassFromForm(values, existing, {
      id: `class-${Date.now()}`,
      code,
      memberCount: 1,
      postCount: 0,
      archived: false,
    });

    set((state) => ({
      classes: [...state.classes, created],
      modalMode: null,
      editingClassId: null,
    }));

    useProfileClassOrganizationStore.getState().onClassCreated(created.id);
    void values.notifyFollowers;
    return created;
  },

  updateClass: (classId, values) => {
    set((state) => ({
      classes: state.classes.map((item) => {
        if (item.id !== classId) {
          return item;
        }
        return buildManagedClassFromForm(values, state.classes, {
          id: item.id,
          code: item.code,
          archived: item.archived,
          postCount: item.postCount,
          memberCount: item.memberCount,
          dotClassName: item.dotClassName,
        });
      }),
      modalMode: null,
      editingClassId: null,
    }));
  },

  archiveClass: (classId) => {
    get().archiveClasses([classId]);
  },

  archiveClasses: (classIds) => {
    if (classIds.length === 0) {
      return;
    }
    const idSet = new Set(classIds);
    set((state) => ({
      classes: state.classes.map((item) =>
        idSet.has(item.id) ? { ...item, archived: true } : item,
      ),
      modalMode: null,
      editingClassId: null,
    }));
    for (const classId of classIds) {
      useProfileClassOrganizationStore.getState().onClassRemoved(classId);
    }
  },

  deleteClass: (classId) => {
    get().deleteClasses([classId]);
  },

  deleteClasses: (classIds) => {
    if (classIds.length === 0) {
      return;
    }
    const idSet = new Set(classIds);
    set((state) => ({
      classes: state.classes.filter((item) => !idSet.has(item.id)),
      modalMode: null,
      editingClassId: null,
    }));
    for (const classId of classIds) {
      useProfileClassOrganizationStore.getState().onClassRemoved(classId);
    }
  },

  restoreClass: (classId) => {
    set((state) => ({
      classes: state.classes.map((item) =>
        item.id === classId ? { ...item, archived: false } : item,
      ),
    }));
  },

  getActiveOwnedCatalogEntries: () => {
    return get()
      .classes.filter((item) => !item.archived)
      .map(managedClassToCatalogEntry);
  },

  getActiveOwnedClassCards: () => {
    return get()
      .classes.filter((item) => !item.archived)
      .map(managedClassToOwnedCard);
  },

  getArchivedClasses: () => {
    return get().classes.filter((item) => item.archived);
  },

  getManagedClassByCode: (code) => {
    const normalized = code.toUpperCase();
    return get().classes.find((item) => item.code.toUpperCase() === normalized);
  },

  getManagedClassById: (id) => {
    return get().classes.find((item) => item.id === id);
  },
}));
